import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  DialogProps,
} from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import React from "react";

interface ConfirmationPanelProps {
  showModal: boolean;
  hideModal: () => void;
  confirmModal: () => void;
  entry?: any;
  type?: string;
  action?: string;
  title?: string;
  content?: string;
  confirmButtonText?: string;
  discardButtonText?: string;
  customActions?: React.ReactNode;
  maxWidth?: DialogProps["maxWidth"];
}

const ConfirmationPanel: React.FC<ConfirmationPanelProps> = ({
  showModal,
  hideModal,
  confirmModal,
  entry,
  type,
  action,
  title,
  content,
  confirmButtonText,
  discardButtonText,
  customActions,
  maxWidth,
}) => {
  const { t } = useTranslation();

  const isDeleteAction = action === "delete";

  const handleClose = () => {
    hideModal();
  };

  // Memoize identifier to avoid recalculation
  const identifier = React.useMemo(() => {
    const identifierMap = {
      meeting: entry?.name,
      item: entry?.identifier || entry?.index,
      user: entry?.email,
      project: entry?.name,
      attachment: entry?.displayName || entry?.name || entry?.url,
    };
    return identifierMap[type as keyof typeof identifierMap];
  }, [type, entry]);

  // Use object mapping instead of if-else chains
  const actionConfig = React.useMemo(() => {
    const configs = {
      delete: {
        title: "text.delete_confirmation",
        content: () => t("text.delete_message", { type, identifier }),
        button: "button.delete",
      },
      navigate: {
        title: "text.navigate_confirmation",
        content: () => t("text.navigate_message", { type, identifier }),
        button: "button.continue",
      },
      archive: {
        title: "text.archive_confirmation",
        content: () => t("text.archive_message", { type, identifier }),
        button: "button.archive",
      },
      bookmark: {
        title: "bookmark.confirmation",
        content: () => t("bookmark.confirmation"),
        button: "button.yes",
      },
      warning: {
        title: "text.warning.warning_confirmation",
        content: () => t("text.warning.warning_message"),
        button: "text.warning.close_without_saving",
      },
    };

    return (
      configs[action as keyof typeof configs] || {
        title: "",
        content: () => "",
        button: "button.continue",
      }
    );
  }, [action, type, identifier, t]);

  const dialogTitle = title || t(actionConfig.title);
  const dialogContent = content || actionConfig.content();
  const resolvedConfirmButtonText = confirmButtonText || t(actionConfig.button);
  const resolvedDiscardButtonText =
    discardButtonText ||
    (action === "warning"
      ? t("text.warning.continue_button")
      : t("button.cancel"));

  return (
    <Dialog
      fullWidth={true}
      maxWidth={maxWidth ?? "xs"}
      onClose={handleClose}
      aria-labelledby="confirmation-dialog"
      open={!!showModal} slotProps={{ paper: {
        style: {
          borderRadius: "8px",
          backgroundColor: "white",
        },
      } }}
    >
      {/* Colored accent bar */}
      <Box>
        <Box
          sx={[{
            width: "8px",
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            borderTopLeftRadius: "inherit",
            borderBottomLeftRadius: "inherit"
          }, isDeleteAction ? {
            backgroundColor: "error.main"
          } : {
            backgroundColor: "white"
          }]}
        />
      </Box>
      <DialogTitle id="confirmation-dialog-title" sx={{ pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" component="span">
            {dialogTitle}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={hideModal}
          sx={theme => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500]
          })}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          sx={[{
            color: "text.secondary"
          }, isDeleteAction ? {
            fontWeight: 500
          } : {
            fontWeight: 400
          }]}
        >
          {dialogContent}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {customActions ? (
          customActions
        ) : (
          <>
            <Button variant="outlined" onClick={hideModal}>
              {resolvedDiscardButtonText}
            </Button>
            <Button
              variant={isDeleteAction ? "outlined" : "contained"}
              color={isDeleteAction ? "error" : "primary"}
              startIcon={isDeleteAction ? <DeleteIcon /> : undefined}
              onClick={confirmModal}
              autoFocus
            >
              {resolvedConfirmButtonText}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationPanel;
